// src/pages/AdminDashboard.jsx
// Main trainer/admin dashboard — attendance overview, student list, diary viewer

import { useState, useEffect, useMemo, memo } from "react";
import AdminProgressView from "./AdminProgressView";
import { 
  getAllStudents, subscribeTodayAttendance, subscribeDiary, 
  getStudentAttendance, updateStudentStatus, markAttendance,
  subscribeCategories, saveCategory, deleteCategory, 
  subscribeTopics, saveTopic, deleteTopic, getNextRollNumber,
  subscribeMessageLogs
} from "../firebase/database";
import { firestore } from "../firebase/config";
import { doc, setDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { createStaffAccount } from "../firebase/auth";
import { todayStr, getInitials } from "../utils/helpers";
import { theme } from "../utils/theme";
import { getVikramSamvat } from "../utils/panchang";

import { addDays, subDays, format, parseISO } from "date-fns";
import AIAssistant from "../components/AIAssistant";
import AIChat from "../components/AIChat";
import StudentProgressCheckup from "./StudentProgressCheckup";
import WhatsAppSender from "../components/WhatsAppSender";
// Accounting
import CustomerList from "./CustomerList";
import ChartOfAccounts from "./ChartOfAccounts";
import InvoiceList from "./InvoiceList";
import ItemMaster from "./ItemMaster";
import ExpenseList from "./ExpenseList";
import JournalVoucher from "./JournalVoucher";
import FinancialReports from "./FinancialReports";
import StudentKiosk from "./StudentKiosk";

const C = theme.colors;


const statusColor = s => {
  const status = (typeof s === "object" ? s.status : s) || "absent";
  return status === "present" ? C.green : status === "absent" ? C.red : C.muted;
};

const BirthdayIcon = ({ dob }) => {
  if (!dob) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dobDate = new Date(dob);
  const bday = new Date(today.getFullYear(), dobDate.getMonth(), dobDate.getDate());
  if (bday < today) bday.setFullYear(today.getFullYear() + 1);
  
  const diff = Math.ceil((bday - today) / (1000 * 60 * 60 * 24));
  if (diff > 7) return null;

  const isToday = diff === 0 || (today.getDate() === dobDate.getDate() && today.getMonth() === dobDate.getMonth());
  
  return (
    <>
      <style>{`
        @keyframes bday-pulse {
          0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(229, 62, 62, 0.4); }
          70% { transform: scale(1.05); box-shadow: 0 0 0 6px rgba(229, 62, 62, 0); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(229, 62, 62, 0); }
        }
        .bday-badge-today { animation: bday-pulse 2s infinite; }
      `}</style>
      <span 
        title={`Birthday: ${dobDate.toLocaleDateString("en-IN", { day: 'numeric', month: 'short' })}`} 
        className={isToday ? "bday-badge-today" : ""}
        style={{ 
          fontSize: 11, 
          fontWeight: 800, 
          marginLeft: 10, 
          color: isToday ? "#FFF" : "#744210",
          background: isToday ? "linear-gradient(135deg, #F56565 0%, #E53E3E 100%)" : "linear-gradient(135deg, #F6E05E 0%, #ECC94B 100%)",
          padding: "3px 10px", 
          borderRadius: 20, 
          border: `1.5px solid ${isToday ? "#C53030" : "#D69E2E"}`,
          display: "inline-flex", 
          alignItems: "center", 
          gap: 4,
          boxShadow: isToday ? "0 4px 6px rgba(229, 62, 62, 0.3)" : "0 2px 4px rgba(214, 158, 46, 0.2)",
          textShadow: isToday ? "0 1px 2px rgba(0,0,0,0.2)" : "none"
        }}
      >
        <span style={{ fontSize: 13 }}>{isToday ? "🎂" : "🎁"}</span>
        {isToday ? "HAPPY BIRTHDAY!" : dobDate.toLocaleDateString("en-IN", { day: 'numeric', month: 'short' })}
      </span>
    </>
  );
};

const StaffItem = memo(({ s, attendance }) => {
  const rec = attendance[s.id];
  const st  = typeof rec === "object" ? rec.status : rec;
  const time = typeof rec === "object" ? rec.time : null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <Avatar src={s.photoURL} initials={getInitials(s.name)} size={30} color={C.purple} />
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{s.name}</div>
          <BirthdayIcon dob={s.dob} />
        </div>
        <div style={{ fontSize: 11, color: C.muted }}>Staff Member</div>
      </div>
      {st ? <Badge color={statusColor(st)}>{st}</Badge> : <Badge color={C.muted} bg={C.dim + "33"}>—</Badge>}
      {time && <span style={{ fontSize: 11, fontFamily: "monospace", color: C.muted }}>{time}</span>}
    </div>
  );
});

const StudentItem = memo(({ s, attendance }) => {
  const rec = attendance[s.id];
  const st  = typeof rec === "object" ? rec.status : rec;
  const time = typeof rec === "object" ? rec.time : null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <Avatar src={s.photoURL} initials={getInitials(s.name)} size={30} color={statusColor(st)} />
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{s.name}</div>
          <BirthdayIcon dob={s.dob} />
        </div>
        <div style={{ fontSize: 11, color: C.muted }}>Roll #{s.roll} · {s.batch}</div>
      </div>
      {st ? <Badge color={statusColor(st)}>{st}</Badge> : <Badge color={C.muted} bg={C.dim + "33"}>—</Badge>}
      {time && <span style={{ fontSize: 11, fontFamily: "monospace", color: C.muted }}>{time}</span>}
    </div>
  );
});

function Badge({ children, color = C.accent, bg }) {
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: bg || color + "1A", color, letterSpacing: .3 }}>{children}</span>;
}

function Avatar({ src, initials, size = 36, color = C.accent }) {
  if (src) {
    return (
      <img 
        src={src} 
        alt={initials} 
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${color}33`, flexShrink: 0 }} 
      />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: color + "1A", border: `1.5px solid ${color}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * .3, fontWeight: 700, color, flexShrink: 0 }}>
      {initials}
    </div>
  );
}

function Stat({ label, value, color = C.accent, sub }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px" }}>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 6, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: C.muted, marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

export default function AdminDashboard({ view: initialView = "overview" }) {
  const { user, role, logout } = useAuth();
  const [view, setView]         = useState(initialView);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const today = todayStr();

  useEffect(() => {
    setView(initialView);
  }, [initialView]);

  useEffect(() => {
    getAllStudents().then(setStudents);
    const unsub = subscribeTodayAttendance(setAttendance, today);
    return unsub;
  }, [today]);

  const activeMembers = students.filter(s => s.status !== "inactive");
  const staff = activeMembers.filter(s => s.role === "staff");
  const studentList = activeMembers.filter(s => s.role !== "staff");

  const presentStaff = staff.filter(s => {
    const rec = attendance[s.id];
    return (typeof rec === "object" ? rec.status : rec) === "present";
  }).length;

  const presentStudents = studentList.filter(s => {
    const rec = attendance[s.id];
    return (typeof rec === "object" ? rec.status : rec) === "present";
  }).length;

  const present = Object.values(attendance).filter(v => (v.status || v) === "present").length;
  const total   = activeMembers.length;
  const pct     = total ? Math.round(present / total * 100) : 0;

  const [peopleTab, setPeopleTab] = useState("all");
  const filteredPeople = peopleTab === "all" ? students : (peopleTab === "staff" ? staff : studentList);

  const [selfProgressStudentId, setSelfProgressStudentId] = useState("");
  const [selfProgressDate, setSelfProgressDate] = useState(today);

  const shiftSelfProgressDate = (days) => {
    try {
      const current = parseISO(selfProgressDate);
      const next = addDays(current, days);
      setSelfProgressDate(format(next, "yyyy-MM-dd"));
    } catch (e) {
      console.error("Date shift error:", e);
    }
  };

  useEffect(() => {
    if (students.length > 0 && !selfProgressStudentId) {
      const defaultStudent = students.find(s => 
        s.name.includes("Pankaj Ojha") && (s.roll === "12" || s.roll === "012")
      );
      if (defaultStudent) {
        setSelfProgressStudentId(defaultStudent.id);
      } else {
        setSelfProgressStudentId(students[0].id);
      }
    }
  }, [students, selfProgressStudentId]);

  const selectedStudentForSelfProgress = students.find(s => s.id === selfProgressStudentId);

  const samvat = useMemo(() => getVikramSamvat(), []);

  return (
    <div className="fade" style={{ width: "100%" }}>
      <style>{`
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}} 
        .fade{animation:fadeIn .3s ease}
        
        .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
        .section-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }

        @media (max-width: 1024px) {
          .stats-grid { grid-template-columns: 1fr 1fr !important; }
          .section-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── OVERVIEW ── */}
      {view === "overview" && (
        <div className="fade">
          <div style={{ marginBottom: 22, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>Good morning 👋</div>
              <div style={{ fontSize: 14, color: C.muted }}>{new Date().toLocaleDateString("en-IN", { dateStyle: "full" })}</div>
            </div>
            {!process.env.REACT_APP_GEMINI_API_KEY && (
              <div style={{ textAlign: "right", borderLeft: `2px solid ${C.accent}33`, paddingLeft: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.accent }}>हिन्दी पंचांग / Life AI</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{samvat.tithi} ({samvat.paksha})</div>
                <div style={{ fontSize: 12, color: C.muted }}>{samvat.month} {samvat.year}</div>
              </div>
            )}
          </div>

          <AIAssistant />

          <div className="stats-grid">
            <Stat label="Total Active" value={total} sub={`${studentList.length} Students · ${staff.length} Staff`} color={C.accent} />
            <Stat label="Present Today" value={present} sub={`${presentStudents} Students · ${presentStaff} Staff`} color={C.green} />
            <Stat label="Absent Today" value={total - present} color={C.red} />
            <Stat label="Overall Rate" value={pct + "%"} sub="Auto-sync on" color={C.purple} />
          </div>

          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Attendance Progress</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: pct >= 80 ? C.green : pct >= 60 ? C.amber : C.red }}>{pct}% Complete</span>
            </div>
            <div style={{ height: 8, borderRadius: 4, background: C.dim }}>
              <div style={{ width: pct + "%", height: "100%", borderRadius: 4, background: pct >= 80 ? C.green : pct >= 60 ? C.amber : C.red, transition: "width .5s" }} />
            </div>
          </div>

          <div className="section-grid">
             {/* Staff Attendance Box */}
             <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Staff Attendance</div>
                <Badge color={C.purple}>{staff.length} Active</Badge>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 500, overflowY: "auto", paddingRight: 4 }}>
                {staff.length === 0 && <div style={{ fontSize: 13, color: C.muted, textAlign: "center", padding: "10px 0" }}>No staff members found.</div>}
                {staff.sort((a, b) => a.name.localeCompare(b.name)).map(s => (
                  <StaffItem key={s.id} s={s} attendance={attendance} />
                ))}
              </div>
            </div>

            {/* Student Attendance Box */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Student Attendance</div>
                <Badge color={C.accent}>{studentList.length} Active</Badge>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 500, overflowY: "auto", paddingRight: 4 }}>
                {studentList.length === 0 && <div style={{ fontSize: 13, color: C.muted, textAlign: "center", padding: "10px 0" }}>No students found.</div>}
                {studentList.sort((a, b) => a.name.localeCompare(b.name)).map(s => (
                  <StudentItem key={s.id} s={s} attendance={attendance} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {view === "progress" && (
        <div className="fade">
          <AdminProgressView />
        </div>
      )}

      {view === "self-progress" && (
        <div className="fade">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>Self Progress Checkup</div>
              <div style={{ fontSize: 14, color: C.muted }}>Manage log entries for individual students</div>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <select 
                value={selfProgressStudentId} 
                onChange={e => setSelfProgressStudentId(e.target.value)}
                style={{ padding: "10px 14px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.text, outline: "none", fontSize: 14 }}
              >
                <option value="">Select Person</option>
                {activeMembers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.roll})</option>)}
              </select>
              <div style={{ display: "flex", background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
                <button 
                  onClick={() => shiftSelfProgressDate(-1)} 
                  style={{ padding: "10px 14px", border: "none", background: "none", color: C.accent, cursor: "pointer", fontWeight: 700, fontSize: 16 }}
                >
                  «
                </button>
                <input 
                  type="date" 
                  value={selfProgressDate} 
                  onChange={e => setSelfProgressDate(e.target.value)}
                  style={{ padding: "10px 14px", border: "none", background: "none", color: C.text, outline: "none", fontSize: 14, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}` }}
                />
                <button 
                  onClick={() => shiftSelfProgressDate(1)} 
                  style={{ padding: "10px 14px", border: "none", background: "none", color: C.accent, cursor: "pointer", fontWeight: 700, fontSize: 16 }}
                >
                  »
                </button>
              </div>
            </div>
          </div>
          {selectedStudentForSelfProgress ? (
            <StudentProgressCheckup
              key={`${selfProgressStudentId}_${selfProgressDate}`}
              studentId={selectedStudentForSelfProgress.id}
              studentName={selectedStudentForSelfProgress.name}
              date={selfProgressDate}
            />
          ) : (
            <div style={{ padding: 60, textAlign: "center", color: C.muted }}>Select a person to check progress.</div>
          )}
        </div>
      )}

      {view === "whatsapp" && (
        <div className="fade">
          <WhatsAppSender />
        </div>
      )}

      {view === "messages" && (
        <div className="fade">
          <MessageLogView />
        </div>
      )}

      {view === "ai-chat" && (
        <div className="fade">
          <AIChat />
        </div>
      )}

      {/* ── Accounting: Customers ── */}
      {view === "customers" && (
        <div className="fade">
          <CustomerList />
        </div>
      )}

      {/* ── Accounting: Chart of Accounts ── */}
      {view === "accounts" && (
        <div className="fade">
          <ChartOfAccounts />
        </div>
      )}

      {/* ── Accounting: Invoices ── */}
      {view === "invoices" && (
        <div className="fade">
          <InvoiceList />
        </div>
      )}

      {view === "items" && (
        <div className="fade">
          <ItemMaster />
        </div>
      )}

      {view === "expenses" && (
        <div className="fade">
          <ExpenseList />
        </div>
      )}

      {/* ── Accounting: Journal Voucher ── */}
      {view === "jv" && (
        <div className="fade">
          <JournalVoucher />
        </div>
      )}

      {/* ── Accounting: Financial Reports ── */}
      {view === "financial-reports" && (
        <div className="fade">
          <FinancialReports />
        </div>
      )}

      {view === "kiosk" && (
        <div className="fade" style={{ margin: "-28px", height: "calc(100vh - 0px)", overflow: "hidden" }}>
          <StudentKiosk />
        </div>
      )}

      {view === "people" && (
        <div className="fade">
          <style>{`
            .people-table-container { background: ${C.card}; border: 1px solid ${C.border}; borderRadius: 12px; overflow: hidden; }
            .people-mobile-cards { display: none; flex-direction: column; gap: 12px; }
            .person-card { background: ${C.card}; border: 1px solid ${C.border}; border-radius: 12px; padding: 16px; display: flex; flex-direction: column; gap: 12px; }
            .person-card-header { display: flex; align-items: center; justify-content: space-between; }
            .person-card-info { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 13px; }
            .info-label { color: ${C.muted}; font-size: 11px; font-weight: 600; text-transform: uppercase; }
            .person-card-actions { display: flex; gap: 8px; flex-wrap: wrap; padding-top: 8px; border-top: 1px solid ${C.border}; }

            @media (max-width: 768px) {
              .people-table-container { display: none; }
              .people-mobile-cards { display: flex; }
            }
          `}</style>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20, flexWrap: "wrap", gap: 16 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>Directory</div>
              <div style={{ fontSize: 14, color: C.muted }}>Manage all staff and students</div>
            </div>
            <div style={{ display: "flex", background: C.dim, padding: 3, borderRadius: 8, gap: 2, overflowX: "auto", maxWidth: "100%" }}>
              {[["all", "All"], ["students", "Students"], ["staff", "Staff"], ["inactive", "Inactive"]].map(([id, label]) => (
                <div key={id} onClick={() => setPeopleTab(id)} style={{ padding: "6px 14px", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer", background: peopleTab === id ? C.surface : "transparent", color: peopleTab === id ? C.accent : C.muted, transition: ".2s", whiteSpace: "nowrap" }}>{label}</div>
              ))}
            </div>
          </div>

          <div className="people-table-container">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
                <thead>
                  <tr style={{ background: C.bg }}>
                    {["Name", "ID / Roll", "Role / Batch", "Phone", "Family Phone", "Face ID", "Today", "Action"].map(h => (
                      <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: C.muted, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(peopleTab === "inactive" ? students.filter(s => s.status === "inactive") : filteredPeople.filter(s => s.status !== "inactive"))
                    .sort((a, b) => {
                      const recA = attendance[a.id];
                      const recB = attendance[b.id];
                      const statusA = (typeof recA === "object" ? recA.status : recA) || "absent";
                      const statusB = (typeof recB === "object" ? recB.status : recB) || "absent";
                      
                      if (statusA === "present" && statusB !== "present") return -1;
                      if (statusA !== "present" && statusB === "present") return 1;
                      return a.name.localeCompare(b.name);
                    })
                    .map((s, i) => {
                    const rec = attendance[s.id];
                    const isStaff = s.role === "staff";
                    const isActive = s.status !== "inactive";
                    return (
                      <tr key={s.id} style={{ borderTop: i > 0 ? `1px solid ${C.border}` : "none", transition: "background .2s", opacity: isActive ? 1 : 0.6 }}>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <Avatar src={s.photoURL} initials={getInitials(s.name)} size={30} color={isStaff ? C.purple : C.accent} />
                            <span style={{ fontSize: 14, fontWeight: 500, color: C.text }}>{s.name}</span>
                          </div>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: C.muted, fontFamily: "monospace" }}>{s.roll}</td>
                        <td style={{ padding: "12px 16px" }}>
                          {isStaff ? <Badge color={C.purple} bg={C.purpleSoft}>Staff</Badge> : <Badge color={C.accent}>{s.batch || "—"}</Badge>}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: C.text }}>{s.phone || "—"}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: C.text }}>{s.familyPhone || "—"}</td>
                        <td style={{ padding: "12px 16px" }}>
                          {s.faceEnrolled ? <Badge color={C.green} bg={C.greenSoft}>✓</Badge> : <Badge color={C.red} bg={C.redSoft}>Pending</Badge>}
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          {rec ? <Badge color={statusColor(rec.status)}>{rec.status}</Badge> : <span style={{ fontSize: 12, color: C.muted }}>—</span>}
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                            {isActive && (rec?.status !== "present" && rec !== "present") && (
                              <button 
                                onClick={async (e) => { 
                                  e.stopPropagation(); 
                                  if (window.confirm(`Mark ${s.name} as Present?`)) {
                                    await markAttendance(s.id, "present");
                                  }
                                }}
                                style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${C.green}`, background: C.green, color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                              >
                                Mark
                              </button>
                            )}
                            <button 
                              onClick={async (e) => { 
                                e.stopPropagation(); 
                                if (window.confirm(`Set ${s.name} as ${isActive ? "Inactive" : "Active"}?`)) {
                                  await updateStudentStatus(s.id, isActive ? "inactive" : "active");
                                  window.location.reload(); 
                                }
                              }}
                              style={{ padding: "4px 6px", borderRadius: 6, border: `1px solid ${isActive ? C.red : C.green}`, background: isActive ? C.redSoft : C.greenSoft, color: isActive ? C.red : C.green, fontSize: 10, fontWeight: 700, cursor: "pointer" }}
                            >
                              {isActive ? "OFF" : "ON"}
                            </button>
                            {isActive && (
                              <>
                                <button 
                                  onClick={(e) => { e.stopPropagation(); window.location.href = `/re-enroll/${s.id}`; }}
                                  style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${C.border}`, background: "#fff", color: C.accent, fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                                >
                                  Face
                                </button>
                                <button 
                                  onClick={(e) => { e.stopPropagation(); window.location.href = `/complete-profile/${s.id}`; }}
                                  style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${C.accent}`, background: C.accent, color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                                >
                                  Prof
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="people-mobile-cards">
            {(peopleTab === "inactive" ? students.filter(s => s.status === "inactive") : filteredPeople.filter(s => s.status !== "inactive"))
              .sort((a, b) => {
                const recA = attendance[a.id];
                const recB = attendance[b.id];
                const statusA = (typeof recA === "object" ? recA.status : recA) || "absent";
                const statusB = (typeof recB === "object" ? recB.status : recB) || "absent";
                if (statusA === "present" && statusB !== "present") return -1;
                if (statusA !== "present" && statusB === "present") return 1;
                return a.name.localeCompare(b.name);
              })
              .map((s) => {
                const rec = attendance[s.id];
                const isStaff = s.role === "staff";
                const isActive = s.status !== "inactive";
                return (
                  <div key={s.id} className="person-card" style={{ opacity: isActive ? 1 : 0.6 }}>
                    <div className="person-card-header">
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Avatar src={s.photoURL} initials={getInitials(s.name)} size={40} color={isStaff ? C.purple : C.accent} />
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{s.name}</div>
                          <div style={{ fontSize: 12, color: C.muted }}>Roll/ID: {s.roll}</div>
                        </div>
                      </div>
                      {rec ? <Badge color={statusColor(rec.status)}>{rec.status}</Badge> : <span style={{ fontSize: 12, color: C.muted }}>—</span>}
                    </div>
                    
                    <div className="person-card-info">
                      <div>
                        <div className="info-label">Role / Batch</div>
                        {isStaff ? <Badge color={C.purple} bg={C.purpleSoft}>Staff</Badge> : <Badge color={C.accent}>{s.batch || "—"}</Badge>}
                      </div>
                      <div>
                        <div className="info-label">Face ID</div>
                        {s.faceEnrolled ? <Badge color={C.green} bg={C.greenSoft}>✓ Enrolled</Badge> : <Badge color={C.red} bg={C.redSoft}>Pending</Badge>}
                      </div>
                      <div>
                        <div className="info-label">Phone</div>
                        <div style={{ fontWeight: 500 }}>{s.phone || "—"}</div>
                      </div>
                      <div>
                        <div className="info-label">Family Phone</div>
                        <div style={{ fontWeight: 500 }}>{s.familyPhone || "—"}</div>
                      </div>
                    </div>

                    <div className="person-card-actions">
                      {isActive && (rec?.status !== "present" && rec !== "present") && (
                        <button 
                          onClick={async (e) => { 
                            e.stopPropagation(); 
                            if (window.confirm(`Mark ${s.name} as Present?`)) {
                              await markAttendance(s.id, "present");
                            }
                          }}
                          style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.green}`, background: C.green, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                        >
                          Mark Present
                        </button>
                      )}
                      <button 
                        onClick={async (e) => { 
                          e.stopPropagation(); 
                          if (window.confirm(`Set ${s.name} as ${isActive ? "Inactive" : "Active"}?`)) {
                            await updateStudentStatus(s.id, isActive ? "inactive" : "active");
                            window.location.reload(); 
                          }
                        }}
                        style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${isActive ? C.red : C.green}`, background: isActive ? C.redSoft : C.greenSoft, color: isActive ? C.red : C.green, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                      >
                        {isActive ? "Deactivate" : "Activate"}
                      </button>
                      {isActive && (
                        <>
                          <button 
                            onClick={(e) => { e.stopPropagation(); window.location.href = `/re-enroll/${s.id}`; }}
                            style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", color: C.accent, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                          >
                            Update Face
                          </button>
                          <button 
                            onClick={(e) => { e.stopPropagation(); window.location.href = `/complete-profile/${s.id}`; }}
                            style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.accent}`, background: C.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                          >
                            Edit Profile
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {view === "kiosks" && (
        <div className="fade">
          <KioskManagement />
        </div>
      )}

      {view === "settings" && (
        <div className="fade">
          <SettingsManagement />
        </div>
      )}
    </div>
  );
}

function SettingsManagement() {
  const [categories, setCategories]   = useState([]);
  const [topics, setTopics]           = useState([]);
  const [newCat, setNewCat]           = useState({ label: "", color: "#3D7EFF" });
  const [newTopic, setNewTopic]       = useState({ label: "", categoryId: "" });
  const [lastRoll, setLastRoll]       = useState("");
  const [editingCat, setEditingCat]   = useState(null); // { id, label, color }
  const [editingTopic, setEditingTopic] = useState(null); // { id, label, categoryId }

  // Staff account creation state
  const [staffEmail, setStaffEmail]   = useState("");
  const [staffPassword, setStaffPassword] = useState("");
  const [staffName, setStaffName]     = useState("");
  const [staffRole, setStaffRole]     = useState("trainer");
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffMsg, setStaffMsg]       = useState({ text: "", type: "" });

  // Student account creation state
  const [allMembers, setAllMembers]   = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [showStaffInOnline, setShowStaffInOnline] = useState(false);
  
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [studentEmail, setStudentEmail] = useState("");
  const [studentPassword, setStudentPassword] = useState("123456");
  const [studentLoading, setStudentLoading] = useState(false);
  const [studentMsg, setStudentMsg]   = useState({ text: "", type: "" });

  useEffect(() => {
    const u1 = subscribeCategories(setCategories);
    const u2 = subscribeTopics(setTopics);
    getNextRollNumber().then(n => setLastRoll(n - 1));
    getAllStudents().then(list => setAllMembers(list));
    return () => { u1(); u2(); };
  }, []);

  // Filtered members for online access creation
  const filteredOnlineMembers = useMemo(() => {
    return allMembers.filter(m => {
      const matchesStatus = showInactive ? true : m.status !== "inactive";
      const matchesType   = showStaffInOnline ? true : m.role !== "staff";
      return matchesStatus && matchesType;
    });
  }, [allMembers, showInactive, showStaffInOnline]);

  const handleAddCat = async () => {
    if (!newCat.label) return;
    await saveCategory(newCat);
    setNewCat({ label: "", color: "#3D7EFF" });
  };

  const handleAddTopic = async () => {
    if (!newTopic.label || !newTopic.categoryId) return;
    await saveTopic(newTopic);
    setNewTopic({ ...newTopic, label: "" });
  };

  const handleUpdateRoll = async () => {
    await setDoc(doc(firestore, "settings", "counters"), { lastRollNumber: parseInt(lastRoll) || 0 }, { merge: true });
    alert("Roll number counter updated!");
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    setStaffLoading(true);
    setStaffMsg({ text: "", type: "" });
    try {
      await createStaffAccount(staffEmail, staffPassword, staffName || "Staff Member", staffRole);
      setStaffMsg({ text: `${staffRole.charAt(0).toUpperCase() + staffRole.slice(1)} account created: ${staffEmail}`, type: "success" });
      setStaffEmail(""); setStaffPassword(""); setStaffName(""); setStaffRole("trainer");
    } catch (err) {
      setStaffMsg({ text: err.message, type: "error" });
    }
    setStaffLoading(false);
  };

  const handleStudentSelect = (id) => {
    setSelectedStudentId(id);
    const s = allMembers.find(x => x.id === id);
    if (s) setStudentEmail(s.email || "");
  };

  const handleCreateOnlineAccess = async (e) => {
    e.preventDefault();
    if (!selectedStudentId) return;
    setStudentLoading(true);
    setStudentMsg({ text: "", type: "" });
    try {
      const s = allMembers.find(x => x.id === selectedStudentId);
      // If staff member, give "staff_limited" role (same access as student)
      const role = s.role === "staff" ? "staff_limited" : "student";
      
      await createStaffAccount(studentEmail, studentPassword, s.name, role, { studentId: selectedStudentId });
      
      // Link online account in member profile
      const coll = s.role === "staff" ? "staff" : "students";
      await setDoc(doc(firestore, coll, selectedStudentId), { hasOnlineAccount: true, onlineEmail: studentEmail }, { merge: true });
      
      setStudentMsg({ text: `Online login created for ${s.name}: ${studentEmail}`, type: "success" });
      setSelectedStudentId(""); setStudentEmail(""); setStudentPassword("123456");
    } catch (err) {
      setStudentMsg({ text: err.message, type: "error" });
    }
    setStudentLoading(false);
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
      <style>{`
        @media (max-width: 768px) {
          .settings-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div className="settings-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, gridColumn: "1 / -1" }}>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Manage Categories</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <input value={newCat.label} onChange={e => setNewCat({ ...newCat, label: e.target.value })} placeholder="Category Label" style={{ flex: 1, padding: 8, borderRadius: 6, border: `1px solid ${C.border}`, outline: "none", fontSize: 13, color: C.text, background: C.bg }} />
            <input type="color" value={newCat.color} onChange={e => setNewCat({ ...newCat, color: e.target.value })} style={{ width: 40, height: 36, border: "none", padding: 0, background: "none", cursor: "pointer" }} />
            <button onClick={handleAddCat} style={{ padding: "8px 16px", background: C.accent, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Add</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {categories.map(c => (
              <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: C.bg, borderRadius: 8 }}>
                {editingCat?.id === c.id ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                    <input 
                      type="color" 
                      value={editingCat.color} 
                      onChange={e => setEditingCat({ ...editingCat, color: e.target.value })} 
                      style={{ width: 28, height: 28, border: "none", padding: 0, background: "none", cursor: "pointer" }} 
                    />
                    <input 
                      value={editingCat.label} 
                      onChange={e => setEditingCat({ ...editingCat, label: e.target.value })} 
                      style={{ flex: 1, padding: "4px 8px", borderRadius: 4, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.card }}
                    />
                    <button onClick={async () => { await saveCategory(editingCat); setEditingCat(null); }} style={{ padding: "4px 8px", background: C.accent, color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 11 }}>Save</button>
                    <button onClick={() => setEditingCat(null)} style={{ padding: "4px 8px", background: C.dim, color: C.text, border: "none", borderRadius: 4, cursor: "pointer", fontSize: 11 }}>Cancel</button>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 12, height: 12, borderRadius: "50%", background: c.color }} />
                      <span style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{c.label}</span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setEditingCat({ id: c.id, label: c.label, color: c.color })} style={{ color: C.accent, background: "none", border: "none", cursor: "pointer", fontSize: 12 }}>✎</button>
                      <button onClick={() => deleteCategory(c.id)} style={{ color: C.red, background: "none", border: "none", cursor: "pointer", fontSize: 12 }}>Delete</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Topic List (Auto-fill)</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            <select value={newTopic.categoryId} onChange={e => setNewTopic({ ...newTopic, categoryId: e.target.value })} style={{ padding: 8, borderRadius: 6, border: `1px solid ${C.border}`, outline: "none", fontSize: 13, color: C.text, background: C.bg }}>
              <option value="">Select Category</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <div style={{ display: "flex", gap: 8 }}>
              <input value={newTopic.label} onChange={e => setNewTopic({ ...newTopic, label: e.target.value })} placeholder="Topic Name (e.g. Python Loops)" style={{ flex: 1, padding: 8, borderRadius: 6, border: `1px solid ${C.border}`, outline: "none", fontSize: 13, color: C.text, background: C.bg }} />
              <button onClick={handleAddTopic} style={{ padding: "8px 16px", background: C.accent, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Add Topic</button>
            </div>
          </div>
          <div style={{ maxHeight: 300, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
            {topics.length === 0 && <div style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: 20 }}>No topics added yet.</div>}
            {topics.map(t => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", background: C.bg, borderRadius: 6, fontSize: 12 }}>
                {editingTopic?.id === t.id ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, width: "100%" }}>
                    <select 
                      value={editingTopic.categoryId} 
                      onChange={e => setEditingTopic({ ...editingTopic, categoryId: e.target.value })}
                      style={{ padding: "4px", borderRadius: 4, border: `1px solid ${C.border}`, fontSize: 11, color: C.text, background: C.card, width: 100 }}
                    >
                      {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                    <input 
                      value={editingTopic.label} 
                      onChange={e => setEditingTopic({ ...editingTopic, label: e.target.value })} 
                      style={{ flex: 1, padding: "4px 8px", borderRadius: 4, border: `1px solid ${C.border}`, fontSize: 12, color: C.text, background: C.card }}
                    />
                    <button onClick={async () => { await saveTopic(editingTopic); setEditingTopic(null); }} style={{ padding: "4px 6px", background: C.accent, color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10 }}>Save</button>
                    <button onClick={() => setEditingTopic(null)} style={{ padding: "4px 6px", background: C.dim, color: C.text, border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10 }}>Cancel</button>
                  </div>
                ) : (
                  <>
                    <span style={{ color: C.text }}>{t.label} <span style={{ color: C.muted }}>({categories.find(c => c.id === t.categoryId)?.label})</span></span>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={() => setEditingTopic({ id: t.id, label: t.label, categoryId: t.categoryId })} style={{ color: C.accent, background: "none", border: "none", cursor: "pointer", fontSize: 10 }}>✎</button>
                      <button onClick={() => deleteTopic(t.id)} style={{ color: C.red, background: "none", border: "none", cursor: "pointer", fontSize: 10 }}>✕</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, gridColumn: "1 / -1" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Roll Number Configuration</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, color: C.muted }}>Last Used Roll Number:</span>
          <input type="number" value={lastRoll} onChange={e => setLastRoll(e.target.value)} style={{ width: 100, padding: 8, borderRadius: 6, border: `1px solid ${C.border}`, outline: "none", fontSize: 14, color: C.text, background: C.bg }} />
          <button onClick={handleUpdateRoll} style={{ padding: "8px 16px", background: C.accent, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Update Counter</button>
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>The next student enrolled will automatically get Roll #{parseInt(lastRoll) + 1 || 1}.</div>
      </div>

      {/* Staff Account Creation */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, gridColumn: "1 / -1" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Create Staff Account</div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>Create accounts for trainers and instructors with different access levels.</div>
        
        {staffMsg.text && (
          <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, border: "1px solid", 
            background: staffMsg.type === "success" ? C.greenSoft : C.redSoft, 
            borderColor: staffMsg.type === "success" ? C.green + "44" : C.red + "44",
            color: staffMsg.type === "success" ? C.green : C.red 
          }}>
            {staffMsg.text}
          </div>
        )}
        
        <form onSubmit={handleCreateStaff}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Full Name</label>
              <input value={staffName} onChange={e => setStaffName(e.target.value)} placeholder="e.g. John Doe"
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, outline: "none", color: C.text }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Role</label>
              <select value={staffRole} onChange={e => setStaffRole(e.target.value)}
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, outline: "none", color: C.text, cursor: "pointer" }}>
                <option value="trainer">Trainer (Full Access)</option>
                <option value="instructor">Instructor (Limited Access)</option>
                <option value="kiosk">Kiosk (Scanner Only)</option>
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Email Address</label>
              <input type="email" value={staffEmail} onChange={e => setStaffEmail(e.target.value)} required placeholder="staff@example.in"
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, outline: "none", color: C.text }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Password</label>
              <input type="password" value={staffPassword} onChange={e => setStaffPassword(e.target.value)} required placeholder="••••••••"
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, outline: "none", color: C.text }} />
            </div>
          </div>
          <button type="submit" disabled={staffLoading} style={{ padding: "10px 20px", background: C.accent, color: "#fff", border: "none", borderRadius: 8, cursor: staffLoading ? "wait" : "pointer", fontSize: 13, fontWeight: 600 }}>
            {staffLoading ? "Creating..." : "Create Account"}
          </button>
        </form>

        <div style={{ marginTop: 20, padding: 16, background: theme.colors.accentSoft, borderRadius: 12, border: `1px solid ${C.accent}33` }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.accent, marginBottom: 4 }}>Role Permissions</div>
          <div style={{ fontSize: 12, color: C.accent, lineHeight: 1.6 }}>
            <div><strong>Trainer:</strong> Full access to all features</div>
            <div><strong>Instructor:</strong> Overview, People, Courses, Enroll, Progress, Timeline, Message Log</div>
            <div><strong>Kiosk:</strong> Only scanner/attendance screen</div>
          </div>
        </div>
      </div>

      {/* Online Access Management */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, gridColumn: "1 / -1" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Online Access Management</div>
            <div style={{ fontSize: 13, color: C.muted }}>Enable portal access for learners and remote staff.</div>
          </div>
          <div style={{ display: "flex", gap: 20 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text, cursor: "pointer" }}>
              <input type="checkbox" checked={showStaffInOnline} onChange={e => setShowStaffInOnline(e.target.checked)} />
              Include Staff
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text, cursor: "pointer" }}>
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
              Include Inactive
            </label>
          </div>
        </div>
        
        {studentMsg.text && (
          <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, border: "1px solid", 
            background: studentMsg.type === "success" ? C.greenSoft : C.redSoft, 
            borderColor: studentMsg.type === "success" ? C.green + "44" : C.red + "44",
            color: studentMsg.type === "success" ? C.green : C.red 
          }}>
            {studentMsg.text}
          </div>
        )}
        
        <form onSubmit={handleCreateOnlineAccess}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Select Member (Students/Staff)</label>
              <select value={selectedStudentId} onChange={e => handleStudentSelect(e.target.value)}
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, outline: "none", color: C.text, cursor: "pointer" }}>
                <option value="">-- Choose Member --</option>
                {filteredOnlineMembers.sort((a,b) => a.name.localeCompare(b.name)).map(s => (
                  <option key={s.id} value={s.id}>{s.name} ({s.roll}) {s.role === 'staff' ? '[Staff]' : ''} {s.status === 'inactive' ? '[Inactive]' : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Email Address</label>
              <input type="email" value={studentEmail} onChange={e => setStudentEmail(e.target.value)} required placeholder="member@example.in"
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, outline: "none", color: C.text }} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Default Password</label>
              <input type="text" value={studentPassword} onChange={e => setStudentPassword(e.target.value)} required placeholder="••••••••"
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, outline: "none", color: C.text }} />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button type="submit" disabled={studentLoading || !selectedStudentId} style={{ width: "100%", padding: "10px 20px", background: C.accent, color: "#fff", border: "none", borderRadius: 8, cursor: (studentLoading || !selectedStudentId) ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600 }}>
                {studentLoading ? "Creating..." : "Enable Online Access ✓"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function KioskManagement() {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [name, setName]         = useState("");
  const [loading, setLoading]   = useState(false);
  const [msg, setMsg]           = useState({ text: "", type: "" });

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg({ text: "", type: "" });
    try {
      await createStaffAccount(email, password, name || "Kiosk Device", "kiosk");
      setMsg({ text: `Kiosk account created: ${email}. Use this to log in on kiosk laptops.`, type: "success" });
      setEmail(""); setPassword(""); setName("");
    } catch (err) {
      setMsg({ text: err.message, type: "error" });
    }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 500 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 6 }}>Kiosk Management</div>
      <div style={{ fontSize: 14, color: C.muted, marginBottom: 24 }}>Create dedicated logins for your kiosk laptops. These accounts only have access to the scanning screen.</div>
      
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 18 }}>Create Kiosk Account</div>
        {msg.text && (
          <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, border: "1px solid", 
            background: msg.type === "success" ? C.greenSoft : C.redSoft, 
            borderColor: msg.type === "success" ? C.green + "44" : C.red + "44",
            color: msg.type === "success" ? C.green : C.red 
          }}>
            {msg.text}
          </div>
        )}
        <form onSubmit={handleCreate}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Kiosk Name / Location</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Laptop 1, Lab Entrance"
              style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 14, outline: "none", color: C.text }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Kiosk Login Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="kiosk1@pankaj.in"
              style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 14, outline: "none", color: C.text }} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Login Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="••••••••"
              style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 14, outline: "none", color: C.text }} />
          </div>
          <button type="submit" disabled={loading} style={{ width: "100%", padding: 12, borderRadius: 8, background: C.accent, color: "#fff", border: "none", fontSize: 14, fontWeight: 600, cursor: loading ? "wait" : "pointer" }}>
            {loading ? "Creating..." : "Create Account"}
          </button>
        </form>
      </div>

      <div style={{ marginTop: 24, padding: 16, background: theme.colors.accentSoft, borderRadius: 12, border: `1px solid ${C.accent}33` }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.accent, marginBottom: 4 }}>Security Note</div>
        <div style={{ fontSize: 12, color: C.accent, lineHeight: 1.5 }}>
          Kiosk accounts are restricted. If someone tries to navigate away from the scanner, they will be automatically redirected to the login page and won't see your student lists or reports.
        </div>
      </div>
    </div>
  );
}

function DiaryViewer({ students }) {
  const [entries, setEntries] = useState({});

  useEffect(() => {
    const unsubs = students.map(s => {
      return subscribeDiary(s.id, data => {
        setEntries(prev => ({ ...prev, [s.id]: data }));
      });
    });
    return () => unsubs.forEach(fn => fn());
  }, [students]);

  const all = students.flatMap(s =>
    (entries[s.id] || []).map(e => ({ ...e, student: s }))
  ).sort((a, b) => b.date.localeCompare(a.date));

  if (all.length === 0) {
    return <div style={{ textAlign: "center", padding: 60, color: C.muted, fontSize: 14 }}>No diary entries found.</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {all.map((e, i) => (
        <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <Avatar initials={getInitials(e.student.name)} size={36} color={C.accent} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{e.student.name}</div>
              <div style={{ fontSize: 12, color: C.muted }}>{e.date} · Roll #{e.student.roll}</div>
            </div>
          </div>
          {e.topics && (
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: C.muted }}>Topics: </span>
              <span style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{e.topics}</span>
            </div>
          )}
          {e.notes && (
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6, background: C.bg, padding: "10px 14px", borderRadius: 8 }}>{e.notes}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function MessageLogView() {
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    return subscribeMessageLogs(setLogs, 100);
  }, []);

  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 6 }}>WhatsApp Message Log</div>
      <div style={{ fontSize: 14, color: C.muted, marginBottom: 24 }}>Real-time status of automated and manual notifications.</div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
            <thead>
              <tr style={{ background: C.bg }}>
                {["Time", "Recipient", "Person", "Message", "Status"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: C.muted, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={log.id} style={{ borderTop: i > 0 ? `1px solid ${C.border}` : "none", fontSize: 13 }}>
                  <td style={{ padding: "12px 16px", whiteSpace: "nowrap", color: C.muted }}>
                    {new Date(log.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    <div style={{ fontSize: 10 }}>{new Date(log.timestamp).toLocaleDateString("en-IN")}</div>
                  </td>
                  <td style={{ padding: "12px 16px", fontWeight: 500 }}>{log.to}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ fontWeight: 500 }}>{log.studentName}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{log.type}</div>
                  </td>
                  <td style={{ padding: "12px 16px", maxWidth: 300, color: C.text }}>{log.body}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <Badge color={log.status === "success" ? C.green : C.red} bg={log.status === "success" ? C.greenSoft : C.redSoft}>
                      {log.status.toUpperCase()}
                    </Badge>
                    {log.error && <div style={{ fontSize: 10, color: C.red, marginTop: 4, maxWidth: 150 }}>{log.error}</div>}
                  </td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan="5" style={{ padding: 40, textAlign: "center", color: C.muted }}>No message logs found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
